// src/data/blogSampleOfferRegistry.ts
//
// BLOG-LEAD-MAGNET-SAMPLES-001 — which free 15-page sample each real blog
// article offers.
//
// ONE explicit, reviewed classification per article. It is content metadata,
// set by a person who read the article — never inferred at runtime from the
// URL, the title or anything the visitor typed:
//   "esa"    → the ESA Pet Care Planner sample
//   "psd"    → the PSD Training Workbook sample
//   "choice" → a genuinely mixed article (e.g. ESA vs service animal); the
//              visitor picks ESA or PSD, and nothing is pre-selected.
//
// Scope: every article listed on /blog — the dynamic /blog/<slug> posts
// (src/mocks), the standalone /blog/<slug> pages, the root-level ESA housing
// guides — plus the three lead-magnet articles, whose classification lives on
// their own records (src/seo/blog/leadMagnetArticles.ts) and is spread in here.
//
// scripts/check-blog-lead-magnet.mjs asserts that every listed article is
// classified exactly once, that nothing else is, and that each classification
// agrees with the article's own title (an ESA-only title can never be "psd",
// a PSD-only title can never be "esa", an ESA-and-PSD title must be "choice").

// Relative (not "@/"): build scripts load this module through jiti without the alias.
import { LEAD_MAGNET_ARTICLES } from "../seo/blog/leadMagnetArticles";

export type BlogSampleOffer = "esa" | "psd" | "choice";

const EXISTING_ARTICLES: Readonly<Record<string, BlogSampleOffer>> = {
  "/blog/can-kittens-be-emotional-support-animals": "esa",
  "/blog/can-rabbits-be-emotional-support-animals": "esa",
  "/blog/bought-an-esa-registration-number-what-to-check-before-using-it-for-housing": "esa",
  "/blog/how-to-ask-your-therapist-about-esa-documentation-at-your-next-visit": "esa",
  "/blog/how-to-prepare-an-esa-evaluation-summary-without-oversharing": "esa",
  "/blog/benefits-of-emotional-support-animals": "esa",
  "/blog/what-is-an-emotional-support-animal": "esa",
  "/blog/can-you-travel-with-emotional-support-animal": "esa",
  "/blog/tenant-faqs-emotional-support-animals": "esa",
  "/blog/choosing-between-esa-and-psd": "choice",
  "/blog/hawaii-esa-housing-laws": "esa",
  "/blog/landlord-faqs-emotional-support-animals": "esa",
  "/blog/can-any-pet-become-an-esa": "esa",
  "/blog/online-esa-letter-vs-in-person-evaluation": "esa",
  "/blog/wisconsin-esa-housing-guide": "esa",
  "/blog/nevada-emotional-support-animal-housing-laws": "esa",
  "/blog/oregon-esa-housing-guide": "esa",
  "/blog/mistakes-first-time-esa-owners-make": "esa",
  "/blog/can-an-esa-improve-workplace-mental-health": "esa",
  "/blog/can-employers-ask-about-your-esa": "esa",
  "/blog/can-couples-share-one-emotional-support-animal": "esa",
  "/blog/can-birds-be-emotional-support-animals": "esa",
  "/blog/esa-myths-that-refuse-to-die": "esa",
  "/blog/emotional-support-animals-daily-life": "esa",
  "/blog/can-an-esa-help-with-social-anxiety": "esa",
  "/blog/science-behind-emotional-support-animals": "esa",
  "/blog/can-seniors-benefit-from-emotional-support-animals": "esa",
  "/blog/can-children-have-emotional-support-animals": "esa",
  "/blog/how-mental-health-professionals-evaluate-esa-requests": "esa",
  "/blog/can-hoas-restrict-emotional-support-animals": "esa",
  "/blog/therapist-vs-psychiatrist-esa-letters": "esa",
  "/blog/same-day-esa-letters-legitimate": "esa",
  "/blog/cheap-esa-letters-red-flags": "esa",
  "/blog/esa-letter-vs-esa-registration": "esa",
  "/blog/best-cat-breeds-for-emotional-support": "esa",
  "/blog/best-dog-breeds-for-emotional-support": "esa",
  "/blog/do-emotional-support-animals-need-training": "esa",
  "/blog/esa-dogs-vs-esa-cats": "esa",
  // ── Dynamic posts — src/mocks/blogPostsVerification.ts ──────────────────
  "/blog/how-esa-letter-verification-works-pawtenant": "esa",
  "/blog/why-pawtenant-best-verifiable-esa-letter-service": "esa",
  "/blog/landlord-verification-esa-letter-guide-2026": "esa",
  "/blog/esa-letter-verification-protects-housing-rights-2026": "esa",

  // ── Dynamic posts — src/mocks/blogPostsExtended2.ts ─────────────────────
  "/blog/hoa-esa-rights-2026": "esa",
  "/blog/multiple-esas-one-apartment-2026": "esa",
  "/blog/esa-friendly-hotels-travel-guide-2026": "esa",
  "/blog/esa-road-trip-guide-2026": "esa",
  "/blog/how-esas-help-ptsd-2026": "esa",
  "/blog/esa-for-anxiety-depression-mental-health-guide-2026": "esa",
  "/blog/esa-letters-for-veterans-2026": "esa",
  "/blog/pet-therapy-vs-esa-difference-2026": "choice",
  "/blog/esa-ohio-2026-guide": "esa",
  "/blog/esa-washington-state-2026-guide": "esa",
  "/blog/esa-illinois-chicago-2026-guide": "esa",
  "/blog/esa-colorado-denver-2026-guide": "esa",
  "/blog/esa-pennsylvania-philadelphia-pittsburgh-2026-guide": "esa",
  "/blog/esa-georgia-atlanta-2026-guide": "esa",
  "/blog/esa-michigan-detroit-grand-rapids-2026-guide": "esa",
  "/blog/esa-arizona-phoenix-tucson-2026-guide": "esa",
  "/blog/esa-virginia-northern-va-richmond-2026-guide": "esa",
  "/blog/esa-new-jersey-newark-jersey-city-2026-guide": "esa",
  "/blog/esa-nyc-apartments-coop-rent-stabilized-2026-guide": "esa",
  "/blog/esa-south-florida-miami-fort-lauderdale-2026-guide": "esa",
  "/blog/esa-maryland-baltimore-2026-guide": "esa",

  // ── Dynamic posts — src/mocks/blogPostsExtended.ts ──────────────────────
  "/blog/esa-complete-guide-2026-whats-updated": "esa",
  "/blog/psd-complete-guide-2026-whats-updated": "psd",
  "/blog/esa-california-2026-update": "esa",
  "/blog/esa-texas-2026-update": "esa",
  "/blog/esa-florida-2026-update": "esa",
  "/blog/esa-new-york-2026-update": "esa",
  "/blog/psd-california-2026-guide": "psd",
  "/blog/psd-texas-2026-guide": "psd",
  "/blog/psd-florida-2026-guide": "psd",
  "/blog/psd-new-york-2026-guide": "psd",

  // ── Dynamic posts — src/mocks/blogPosts.ts ──────────────────────────────
  "/blog/how-to-request-esa-accommodation-university-2026": "esa",
  "/blog/can-you-have-esa-in-college-dorms": "esa",
  "/blog/esa-letter-college-students-dorm-rights-federal-laws": "esa",
  "/blog/renters-insurance-pet-owners-complete-guide": "esa",
  "/blog/flying-with-esa-travel-letter-guide": "esa",
  "/blog/how-to-get-esa-letter-from-doctor": "esa",
  "/blog/what-landlords-cannot-legally-do-esa": "esa",
  "/blog/esa-letter-cost-california-2026": "esa",
  "/blog/texas-esa-landlord-rights-2026": "esa",
  "/blog/florida-esa-letter-renters-2026": "esa",
  "/blog/new-york-esa-letter-apartment-2026": "esa",
  "/blog/exotic-esa-rules-whats-realistically-allowed": "esa",
  "/blog/florida-esa-laws-what-landlords-can-ask-for": "esa",
  "/blog/new-york-esa-laws-housing-protections": "esa",
  "/blog/california-esa-laws-explained-2025-update": "esa",
  "/blog/are-online-esa-letters-legit": "esa",
  "/blog/what-a-legit-esa-letter-looks-like-checklist-examples": "esa",
  "/blog/how-to-choose-the-right-esa-for-your-needs": "esa",
  "/blog/how-to-get-certification-for-emotional-support-dog": "esa",
  "/blog/how-to-renew-esa-letter-2026": "esa",
  "/blog/esa-housing-denial-rights-2026": "esa",
  "/blog/esa-letter-college-dorms-2026-complete-guide": "esa",
  "/blog/esa-vs-service-animal-key-differences-2026": "choice",
  "/blog/can-landlord-evict-me-for-having-esa-2026": "esa",
  "/blog/mental-health-conditions-qualify-esa-2026": "esa",
  "/blog/esa-letter-apartments-complete-renters-guide-2026": "esa",
  "/blog/esa-travel-readiness-consultation-guide-2026": "esa",
  "/blog/esa-airline-compliance-guide-all-airlines-2026": "esa",
  "/blog/esa-annual-renewal-subscription-stay-compliant-2026": "esa",

  // ── Standalone /blog pages — ESA letter cluster (BlogArticleKit) ────────
  "/blog/esa-letter-requirements": "esa",
  "/blog/can-primary-care-doctor-write-esa-letter": "esa",
  "/blog/how-to-request-esa-accommodation-from-landlord": "esa",
  "/blog/common-reasons-esa-letters-get-rejected": "esa",
  "/blog/when-should-you-renew-your-esa-letter": "esa",
  "/blog/can-puppies-qualify-as-esas": "esa",

  // ── Standalone /blog pages — pet rent (BlogProse / StatePetRentBlog) ────
  // Housing-cost articles for renters with an ESA: the pet care planner's
  // vaccination and licensing records are the relevant sample.
  "/blog/pet-rent-explained": "esa",
  "/blog/apartment-pet-rent-and-esa-letters": "esa",
  "/blog/pet-deposit-vs-pet-rent": "esa",
  "/blog/california-pet-rent-and-esa-letters": "esa",
  "/blog/new-york-pet-rent-and-esa-letters": "esa",
  "/blog/florida-pet-rent-and-esa-letters": "esa",
  "/blog/texas-pet-rent-and-esa-letters": "esa",
  "/blog/washington-pet-rent-and-esa-letters": "esa",
  "/blog/colorado-pet-rent-and-esa-letters": "esa",

  // ── Standalone /blog pages — PSD (BlogProse) ────────────────────────────
  "/blog/psychiatric-service-dog-letter-explained": "psd",
  "/blog/psd-letter-vs-service-dog-certificate": "psd",
  "/blog/psd-letter-for-anxiety": "psd",
  "/blog/psychiatric-service-dog-housing-rights": "psd",

  // ── Standalone /blog pages — bespoke markup ─────────────────────────────
  "/blog/2026-hud-esa-guidelines": "esa",
  "/blog/emotional-support-animal-travel-anxiety": "esa",
  "/blog/temporary-housing-emotional-support-animal": "esa",
  "/blog/crowds-travel-stress-emotional-support-animal": "esa",
  "/blog/how-to-get-an-esa-letter-online": "esa",
  "/blog/what-is-an-esa-letter": "esa",
  "/blog/how-to-train-psychiatric-service-dog-tasks": "psd",
  // Public-access rights and misrepresentation penalties for service animals:
  // the PSD workbook's ADA reference and access-denial log are the relevant sample.
  "/blog/texas-service-animal-laws-penalties": "psd",
  "/blog/can-anxiety-qualify-you-for-a-psd": "psd",
  "/blog/can-depression-qualify-psychiatric-service-dog": "psd",
  "/blog/can-depression-qualify-you-for-an-esa": "esa",

  // ── Root-level ESA housing guides (src/seo/blog/esaHousingGuides.ts) ────
  "/what-documents-do-landlords-require-for-esa": "esa",
  "/esa-housing-approval-process": "esa",
  "/esa-housing-mistakes-to-avoid": "esa",
};

/** Every classified article route → its offer. */
export const BLOG_SAMPLE_OFFERS: Readonly<Record<string, BlogSampleOffer>> = {
  ...EXISTING_ARTICLES,
  ...Object.fromEntries(LEAD_MAGNET_ARTICLES.map((a) => [a.route, a.sampleOffer])),
};

/** The offer for an exact article route, or null (render nothing). */
export function blogSampleOfferFor(pathname: string): { route: string; offer: BlogSampleOffer } | null {
  const route = (pathname || "/").toLowerCase().replace(/\/+$/, "") || "/";
  const offer = BLOG_SAMPLE_OFFERS[route];
  return offer ? { route, offer } : null;
}
